import { createContext, useContext, useState, useCallback, ComponentType } from "react";

export interface DrawerProps {
    name?: string;
    position?: "left" | "right" | "top" | "bottom";
    onBack?: () => void;
    [key: string]: any;
}

interface DrawerState {
    isOpen: boolean;
    component: ComponentType<any> | null;
    props: DrawerProps;
}

interface ShowDrawerFn {
    <T extends DrawerProps>(component: ComponentType<T>, props: T): void;
    (name: string): void;
}

interface DrawerContextValue {
    showDrawer: ShowDrawerFn;
    hideDrawer: (name?: string) => void;
    isDrawerActive: (name: string) => boolean;
    drawerState: DrawerState | null;
}

const DrawerContext = createContext<DrawerContextValue | null>(null);

export function DrawerProvider({ children }: { children: React.ReactNode }): JSX.Element {
    const [drawerState, setDrawerState] = useState<DrawerState | null>(null);

    const showDrawer = useCallback(
        (componentOrName: ComponentType<any> | string, props?: DrawerProps) => {
            if (typeof componentOrName === "string") {
                setDrawerState({
                    isOpen: true,
                    component: null,
                    props: { name: componentOrName },
                });
            } else {
                setDrawerState({
                    isOpen: true,
                    component: componentOrName as ComponentType<any>,
                    props: props ?? {},
                });
            }
        },
        [],
    ) as ShowDrawerFn;

    const hideDrawer = useCallback((name?: string) => {
        if (!name || drawerState?.props.name === name) {
            setDrawerState(null);
        }
    }, [drawerState]);

    const isDrawerActive = useCallback(
        (name: string): boolean => {
            return !!(drawerState?.isOpen && drawerState?.props.name === name);
        },
        [drawerState]
    );

    return (
        <DrawerContext.Provider value={{ showDrawer, hideDrawer, isDrawerActive, drawerState }}>
            {children}
            {drawerState?.isOpen && drawerState.component && (
                <drawerState.component {...drawerState.props} />
            )}
        </DrawerContext.Provider>
    );
}

export function useDrawer(): DrawerContextValue {
    const context = useContext(DrawerContext);
    if (!context) {
        throw new Error("useDrawer must be used within DrawerProvider");
    }
    return context;
}


